import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Zap } from 'lucide-react';
import '../pages/Landing.css';

const Pricing: React.FC = () => {
  const plans = [
    {
      name: 'FREE',
      price: '$0',
      period: '/month',
      features: [
        '10 requests per minute',
        '1 IP address',
        '16,384 tokens per request',
        '5 AI models access',
        'Basic support',
        'Community access'
      ],
      cta: 'Get Started',
      link: '/register',
      popular: false
    },
    {
      name: 'PRO',
      price: '$30',
      period: '/month',
      features: [
        '100 requests per minute',
        '3 IP addresses',
        '32,768 tokens per request',
        'All AI models (20+)',
        'Priority support',
        'Advanced analytics',
        'Email support',
        'API webhooks'
      ],
      cta: 'Start Free Trial',
      link: '/register',
      popular: true
    },
    {
      name: 'ENTERPRISE',
      price: '$200',
      period: '/month',
      features: [
        '6,000 requests per minute',
        '30 IP addresses',
        '65,536 tokens per request',
        'All AI models (20+)',
        '24/7 dedicated support',
        'Custom integration',
        'SLA guarantee',
        'Dedicated account manager',
        'Custom models',
        'On-premise deployment'
      ],
      cta: 'Contact Sales',
      link: '/register',
      popular: false
    }
  ];

  return (
    <div className="landing">
      {/* Header */}
      <header className="landing-header">
        <div className="container">
          <div className="header-content">
            <Link to="/" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <ArrowLeft size={20} />
              Back to Home
            </Link>
            <nav className="header-nav">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/login" className="nav-link">Login</Link>
              <Link to="/register" className="btn btn-primary">Get Started</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="pricing" style={{ paddingTop: '8rem' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-description">
              Choose the plan that fits your needs. All plans include access to our professional AI gateway.
            </p>
          </div>
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <div className="pricing-header">
                  <div className="plan-name">{plan.name}</div>
                  <div className="plan-price">
                    <span className="price">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                </div>
                <ul className="features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <CheckCircle size={16} className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link 
                  to={plan.link} 
                  className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'} btn-block`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div style={{ marginTop: '4rem', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1.5rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="card">
                <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>How do I get a redeem code?</h4>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
                  After purchasing a plan, you'll receive a redeem code via email. Use this code to activate your API key in the dashboard.
                </p>
              </div>
              <div className="card">
                <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Can I upgrade or downgrade my plan?</h4>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
                  Yes! You can change your plan at any time. Upgrades take effect immediately, while downgrades apply at the start of your next billing cycle.
                </p>
              </div>
              <div className="card">
                <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>What happens if I exceed my rate limit?</h4>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
                  Requests beyond your rate limit will receive a 429 error. You can upgrade your plan for higher limits or wait for the rate limit to reset.
                </p>
              </div>
              <div className="card">
                <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>What is your refund policy?</h4>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
                  All sales are final. We do not offer refunds. Please test with the FREE plan before upgrading to ensure our service meets your needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" style={{ marginTop: '4rem' }}>
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo">
                <Zap className="logo-icon" />
                <span className="logo-text">Flazu API</span>
              </div>
              <p className="footer-description">
                Professional AI Gateway for Modern Developers. Access 20+ AI models through one unified, secure, and lightning-fast API.
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <Link to="/pricing">Pricing</Link>
                <Link to="/#features">Features</Link>
                <Link to="/">Documentation</Link>
                <Link to="/">API Reference</Link>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <Link to="/">About Us</Link>
                <Link to="/">Contact</Link>
                <Link to="/">Support</Link>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <Link to="/terms">Terms of Service</Link>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/cookies">Cookie Policy</Link>
                <Link to="/dmca">DMCA</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-copyright">
              <p>&copy; 2024 Flazu API. All rights reserved.</p>
            </div>
            <div className="footer-legal">
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/cookies">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;